import { Injectable } from '@angular/core';
import { RxStompState } from '@stomp/rx-stomp';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { AuthState } from '../../shared/models/auth/auth-state.model';
import { AuthStoreService } from '../../shared/services/auth/auth-store.service';
import { Banner } from '../models/banner.model';
import { ApiService } from './api.service';
import { AppConfig } from './app-config.service';
import { BannerService } from './banner.service';

/**
 * Banners are created on the backend during PUT /auth/logged-in and pushed to /user/topic/banner.
 * The subscription has to be active before that call, otherwise the message is dropped.
 */
@Injectable({
  providedIn: 'root',
})
export class BannerRealtimeService {
  private subscription?: Subscription;
  private initialized = false;

  constructor(
    private api: ApiService,
    private authStore: AuthStoreService,
    private bannerService: BannerService,
    private appConfig: AppConfig,
  ) {
  }

  public init(): void {
    if (this.initialized || this.appConfig.config.demo) {
      return;
    }
    this.initialized = true;

    this.authStore.state$
      .pipe(
        filter((state) => state === AuthState.AUTHENTICATED),
        switchMap(() => this.api.stompClientStatus$.pipe(
          filter((status) => status === RxStompState.OPEN),
          take(1),
        )),
      )
      .subscribe(() => this.start());

    this.authStore.logout$.subscribe(() => this.stop());
  }

  /** Completes once the banner topic is subscribed, so logged-in can publish into an active listener. */
  public ready(): Observable<void> {
    if (this.appConfig.config.demo) {
      return of(undefined);
    }
    this.init();
    return this.api.stompClientStatus$.pipe(
      filter((status) => status === RxStompState.OPEN),
      take(1),
      timeout(10000),
      tap(() => this.start()),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  private start(): void {
    if (this.subscription) {
      return;
    }
    this.subscription = this.api.subscribe<Banner>('banner')
      .pipe(filter((banner): banner is Banner => !!banner?.bannerId))
      .subscribe((banner) => this.bannerService.publishIncoming(banner));
  }

  private stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }
}
